import type { UseApiFetchOptions } from "@edco/apollo/dist/runtime/composables/useApiFetch";
import type { FetchError } from "ofetch";
import type { NitroFetchOptions } from "nitropack";
import type { AsyncData } from "#app";
import type { NferProduct, NferProductSearchRequest, NferProductSearchResultDto } from "~/types/product";

type SubmitPoaInput = {
  productKey?: string;
  productId?: number;
};

/**
 * A resource for making product related API calls to the Nfer API
 */
export function useNferProductResource () {
  /**
   * List all products from the Nfer API.
   */
  function listProducts (): AsyncData<NferProduct[], FetchError | null> {
    const url = "/api/nfer/products";
    const options: UseApiFetchOptions<NferProduct[]> = {
      method: "GET",
      headers: { Accept: "application/json" },
    };
    return useApiFetch<NferProduct[]>( url, options );
  }

  /**
   * Search for products on the Nfer site with various filters.
   */
  function searchProducts ( request: MaybeRef<NferProductSearchRequest> ): UseApiFetchResponse<NferProductSearchResultDto> {
    const computedUrl = computed( () => {
      const url = "/api/nfer/products/search";

      const params: Record<string, string> = {
        productThemeId: String( request.productThemeId ?? "" ),
        attributeId: String( request.attributeId ?? "" ),
        subjects: String( request.subjects ?? "" ),
        years: String( request.years ?? "" ),
        terms: String( request.terms ?? "" ),
        types: String( request.types ?? "" ),
        pageNum: `${request.page || 0}`,
        searchTerm: String( request.searchTerm ?? "" ),
      };

      const filteredParams = Object.entries( params )
        .filter( ( [_, value] ) => value != null && value !== "" )
        .map( ( [key, value] ) => `${encodeURIComponent( key )}=${encodeURIComponent( value )}` )
        .join( "&" );

      return `${url}${filteredParams ? `?${filteredParams}` : ""}`;
    } );
    const options: UseApiFetchOptions<NferProductSearchResultDto> = {
      method: "GET",
      headers: { Accept: "application/json" },
      watch: [computedUrl],
    };

    return useApiFetch<NferProductSearchResultDto>( computedUrl, options );
  }

  /**
   * Fetch products based on a list of keys.
   */
  function listProductsByKey ( keys: string[] ): AsyncData<NferProduct[], FetchError | null> {
    const url = "/api/nfer/products/keys";
    const options: UseApiFetchOptions<NferProduct[]> = {
      method: "GET",
      headers: { Accept: "application/json" },
      params: { keys },
    };
    return useApiFetch<NferProduct[]>( url, options );
  }

  /**
   * Fetch all product attributes from the Nfer API.
   */
  function listProductAttributes (): AsyncData<Record<string, string[]>, FetchError | null> {
    const url = "/api/nfer/products/attributes";
    const options: UseApiFetchOptions<Record<string, string[]>> = {
      method: "GET",
      headers: { Accept: "application/json" },
    };
    return useApiFetch<Record<string, string[]>>( url, options );
  }

  return {
    listProducts,
    listProductsByKey,
    listProductAttributes,
    searchProducts,
  };
}

export function $nferProductResource () {
  const dependencies = useFetchDependencies();

  function submitPoa ( payload: SubmitPoaInput ) {
    let url = "/api/nfer/products/poa";

    url = url.replace( /[?&]$/, "" );

    const options: NitroFetchOptions<string> = {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
      body: payload,
    };

    return $apiFetch( url, options, dependencies );
  }

  /**
   * Search for products on the Nfer site with various filters.
   */
  function searchProducts ( {
    productThemeId = null,
    attributeId = null,
    subjects = null,
    years = null,
    terms = null,
    types = null,
    page = 0,
    searchTerm = null,
  }: NferProductSearchRequest ): AsyncData<NferProductSearchResultDto, FetchError | null> {
    const url = "/api/nfer/products/search";
    const params: Record<string, string> = {
      productThemeId: String( productThemeId ?? "" ),
      attributeId: String( attributeId ?? "" ),
      subjects: String( subjects ?? "" ),
      years: String( years ?? "" ),
      terms: String( terms ?? "" ),
      types: String( types ?? "" ),
      pageNum: `${page || 0}`,
      searchTerm: String( searchTerm ?? "" ),
    };

    const filteredParams = Object.entries( params )
      .filter( ( [_, value] ) => value != null && value !== "" )
      .map( ( [key, value] ) => `${encodeURIComponent( key )}=${encodeURIComponent( value )}` )
      .join( "&" );

    const fullUrl = `${url}${filteredParams ? `?${filteredParams}` : ""}`;
    const options: UseApiFetchOptions<NferProductSearchResultDto> = {
      method: "GET",
      headers: { Accept: "application/json" },
    };

    return $apiFetch<NferProductSearchResultDto>( fullUrl, options );
  }

  return {
    submitPoa,
    searchProducts,
  };
}
